import React, { useContext, useState } from 'react';
import { Formik } from 'formik';
import { TextField, Button, Theme, IconButton } from '@mui/material';
import { ChangePasswordForm, ErrorCodes, HttpStatuses } from '../types';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import passwordRecovery from '../lib/services/passwordRecovery';
import Alert from '@mui/lab/Alert';
import ClearIcon from '@mui/icons-material/Clear';
import { GlobalErrorContext } from '../context';

const useStyles = makeStyles((theme: Theme) => ({
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1)
  },
  submit: {
    margin: theme.spacing(3, 0, 2)
  }
}));

interface Props {
  token: string;
  handleSuccess: () => void;
  handleFailure: () => void;
}

/**
 * Password Reset Form
 * @param props
 */
export const PasswordResetForm: React.FC<Props> = ({
  token,
  handleSuccess,
  handleFailure
}) => {
  const classes = useStyles();
  const { t } = useTranslation('translation');
  const [notFoundError, setNotFoundError] = useState('');
  const { setGlobalError } = useContext(GlobalErrorContext);
  return (
    <Formik
      initialValues={{} as ChangePasswordForm}
      onSubmit={async values => {
        const response = await passwordRecovery(token, {
          password: values.password
        });
        if (response.status === HttpStatuses.success) {
          handleSuccess();
        } else {
          if (response.error) {
            if (response.error.code !== ErrorCodes.UNAUTHORIZED_USER) {
              switch (response.error.code) {
                case ErrorCodes.NOT_FOUND:
                  let code = t(response.error.error) ?? t('tokenNotFoundError');
                  setNotFoundError(code);
                  return;
                default:
                  setGlobalError(response.error);
                  return;
              }
            }
            return;
          }
          handleFailure();
        }
      }}
      validate={values => {
        let errors: { [key: string]: any } = {};
        if (!values.password) {
          errors.password = t('enterValue');
        }
        if (values.password.length < 8) {
          errors.password = t('passwordLength');
        }
        if (!values.confirmedPassword) {
          errors.confirmedPassword = t('enterValue');
        }
        if (values.password && values.confirmedPassword) {
          if (values.password !== values.confirmedPassword) {
            errors.confirmedPassword = t('errorConfirmedPassword');
          }
        }
        return errors;
      }}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        errors,
        isValid,
        isSubmitting
      }) => (
        <form onSubmit={handleSubmit} className={classes.form} noValidate>
          <TextField
            variant="standard"
            margin="normal"
            required
            fullWidth
            label={t('newPassword')}
            name="password"
            type="password"
            autoFocus
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.password}
            helperText={errors.password}
          />
          <TextField
            variant="standard"
            margin="normal"
            required
            fullWidth
            label={t('confirmedPassword')}
            name="confirmedPassword"
            type="password"
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.confirmedPassword}
            helperText={errors.confirmedPassword}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
            disabled={isSubmitting || !isValid}
          >
            {t('setupPassword')}
          </Button>
          {notFoundError && (
            <Alert
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setNotFoundError('');
                  }}
                >
                  <ClearIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {t('tokenNotFoundError')}
            </Alert>
          )}
        </form>
      )}
    </Formik>
  );
};
